import React, { FC, useState } from "react";
import { Button } from "react-bootstrap";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { registerObject } from "go-form";
import { formatDateToString, formatStringToDate } from "go-core/components/Formatters/FormatDate";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import FormNumberInput from "go-form/components/FormNumberInput";
import {
	ProductApi,
	ProductComponentApi,
	ProductRecipeApi,
} from "../../../../../../../../../services/Api/Organization/types";
import { onParseProductRecipes } from "../../Tabs/ProductEdit/ProductEdit";
import ProductRecipeButtons from "./ProductRecipeButtons";
import ProductRecipeItemsForm from "./ProductRecipeItemsForm";

interface Props {
	form: UseFormReturn<ProductApi>;
	setCurrentRecipeIndex: (value: number) => void;
	currentRecipeIndex: number;
}

const ProductPackagesForm: FC<Props> = ({ form, setCurrentRecipeIndex, currentRecipeIndex }) => {
	const { t } = useTranslation();
	const {
		control,
		watch,
		setValue,
		formState: { errors },
		register,
	} = form;
	const { remove } = useFieldArray({
		control,
		name: "recipes",
		keyName: "key",
	});
	const watchedRecipes = onParseProductRecipes(watch()).recipes || [];
	const [showRecipesHistory, setShowRecipesHistory] = useState<boolean>(
		!!(watchedRecipes && watchedRecipes?.length > 1)
	);

	const handleDateUpdate = (value: string) => {
		const newRecipes = [...watchedRecipes];

		if (!newRecipes) {
			return;
		}

		const newIndex = newRecipes.findIndex((recipe) => recipe.valid_from === value);
		if (newIndex !== -1) {
			setValue("recipes", newRecipes);
			setCurrentRecipeIndex(newIndex);
		}
	};

	const onShowHistory = () => {
		setShowRecipesHistory(true);
		if (watchedRecipes?.length === 1 && Object.keys(watchedRecipes[0]?.components[0])?.length === 0) {
			registerObject(register, "recipes.0", []);
			setValue(`recipes.0`, { components: [{} as ProductComponentApi] } as ProductRecipeApi);
			setValue(`recipes.1`, {
				valid_from: formatDateToString(new Date()),
				components: [{} as ProductComponentApi],
			} as ProductRecipeApi);
		} else if (watchedRecipes?.length === 1) {
			setValue(`recipes.1.valid_from`, formatDateToString(new Date()));
			setValue(`recipes.1.components`, watchedRecipes[0].components);
		}
		setCurrentRecipeIndex(1);
	};

	const onAddRecipe = () => {
		if (!watchedRecipes) return;

		const prevDate = formatStringToDate(
			watchedRecipes[currentRecipeIndex].valid_from || formatDateToString(new Date())
		);
		const newDate = new Date() > prevDate ? new Date() : prevDate;
		setValue(`recipes.${watchedRecipes.length}`, {
			valid_from: formatDateToString(newDate),
			recipe_amount: null,
			components: watchedRecipes[currentRecipeIndex].components,
		});
		setCurrentRecipeIndex(watchedRecipes.length - 1);
	};

	const onRemoveRecipe = (index: number) => {
		if (watchedRecipes?.length === 2) {
			setShowRecipesHistory(false);
		}
		setCurrentRecipeIndex(Math.max(currentRecipeIndex - 1, 0));
		remove(index);
	};

	return (
		<fieldset className="form-group">
			<div className="d-flex justify-content-between">
				<div className="d-flex flex-column">
					<h5 className="mb-1">{t("modules.product.field.recipe.title")}</h5>
					<span className="text-muted mb-3 d-block">{t("modules.product.field.recipe.help_text")}</span>
				</div>
				<div>
					{!showRecipesHistory && (
						<Button onClick={onShowHistory}>{t("modules.product.action.show_history.title")}</Button>
					)}
				</div>
			</div>
			{showRecipesHistory && (
				<ProductRecipeButtons
					recipes={watchedRecipes || []}
					currentRecipeIndex={currentRecipeIndex}
					onRemove={onRemoveRecipe}
					onCreate={onAddRecipe}
					setCurrentRecipe={setCurrentRecipeIndex}
				/>
			)}
			<div className="d-flex mt-2">
				<div className="me-3">
					<FormNumberInput
						control={control}
						errors={errors}
						suffix=""
						key={currentRecipeIndex}
						name={`recipes.${currentRecipeIndex}.recipe_amount`}
						defaultValue={watch(`recipes.${currentRecipeIndex}.recipe_amount`)}
						label={t("modules.product.field.recipe_amount.title")}
					/>
				</div>
				{currentRecipeIndex > 0 && (
					<FormDatePicker
						name={`recipes.${currentRecipeIndex}.valid_from`}
						control={control}
						key={currentRecipeIndex}
						onApply={(value) => handleDateUpdate(value as string)}
						shouldParseDateToStringOnApply
						label={t("modules.product.field.valid_from.title")}
						defaultValue={watch(`recipes.${currentRecipeIndex}.valid_from`)}
						errors={errors}
					/>
				)}
			</div>
			<ProductRecipeItemsForm watchedRecipes={watchedRecipes} form={form} recipeIndex={currentRecipeIndex} />
		</fieldset>
	);
};

export default ProductPackagesForm;
